import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    CircularProgress,
    Alert
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import HelpIcon from '@mui/icons-material/Help';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

/**
 * Props for the ContactModal component.
 */
interface ContactModalProps {
    /** Whether the modal is currently open. */
    open: boolean;
    /** Callback function to be called when the modal is closed. */
    onClose: () => void;
    /** The type of contact request, either 'help' or 'bug'. */
    type: 'help' | 'bug';
}

/**
 * A modal component for users to submit help requests or bug reports.
 * It allows users to enter a subject and a message, which are then sent to a support email.
 *
 * @param {ContactModalProps} { open, onClose, type } - The props for the component.
 * @returns {React.FC<ContactModalProps>} The ContactModal component.
 */
const ContactModal: React.FC<ContactModalProps> = ({ open, onClose, type }) => {
    const { user } = useAuth();
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (open) {
            setSubject(type === 'bug' ? 'Bug sur la page ' + window.location.pathname : 'Demande d\'aide');
            setMessage('');
            setError(null);
            setSuccess(false);
        }
    }, [open, type]);

    const handleSubmit = async () => {
        if (!subject.trim() || !message.trim()) {
            setError('Veuillez remplir tous les champs');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            await api.post('/api/emails/contact', {
                type,
                subject,
                message,
                pageUrl: window.location.href,
                userInfo: {
                    id: user?._id,
                    name: `${user?.firstName} ${user?.lastName}`,
                    email: user?.email,
                    role: user?.role
                }
            });

            setSuccess(true);
            setTimeout(() => {
                onClose();
            }, 2000);
        } catch (err) {
            console.error(err);
            setError('Erreur lors de l\'envoi. Veuillez réessayer.');
        } finally {
            setLoading(false);
        }
    };

    const title = type === 'bug' ? 'Signaler un Bug' : 'Demande d\'Aide';
    const icon = type === 'bug' ? <BugReportIcon color="error" /> : <HelpIcon color="primary" />;

    return (
        <Dialog open={open} onClose={!loading ? onClose : undefined} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {icon}
                {title}
            </DialogTitle>
            <DialogContent>
                {success ? (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        Votre message a été envoyé avec succès !
                    </Alert>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Votre message sera envoyé à l'équipe support (lms@edu-jalm.fr).
                            Les informations techniques de la page actuelle seront jointes automatiquement.
                        </Typography>

                        {error && <Alert severity="error">{error}</Alert>}

                        <TextField
                            label="Sujet"
                            fullWidth
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            disabled={loading}
                        />

                        <TextField
                            label="Message"
                            fullWidth
                            multiline
                            rows={4}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder={type === 'bug' ? "Décrivez le problème rencontré..." : "Comment pouvons-nous vous aider ?"}
                            disabled={loading}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                {!success && (
                    <>
                        <Button onClick={onClose} disabled={loading}>
                            Annuler
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} /> : null}
                        >
                            Envoyer
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ContactModal;
